/**
 * Axios API client with automatic authentication and token refresh.
 * Directly communicates with the backend API, bypassing NextJS.
 *
 * Token storage is delegated to lib/auth-token so all consumers stay in
 * sync via subscribeToTokens().
 */

import axios, { type InternalAxiosRequestConfig, type AxiosError } from 'axios';
import type {
  AuthTokens,
  LoginCredentials,
  LoginResponse,
  RefreshTokenResponse,
} from '@/types/auth';
import { isTokenExpired } from '@/lib/auth-utils';
import {
  clearStoredTokens,
  getStoredTokens,
  setStoredTokens,
} from '@/lib/auth-token';

// API base URL — fail loudly in production builds if not provided.
const API_BASE_URL: string = (() => {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) {
    console.warn(
      '[api-client] VITE_API_BASE_URL not set; falling back to dev URL. ' +
        'Set VITE_API_BASE_URL in .env.local to silence this warning.'
    );
    return 'https://dev.eo.lecole.ai/api/';
  }
  // In a production build, refusing to start is safer than silently
  // routing PHI to the dev environment.
  throw new Error(
    '[api-client] VITE_API_BASE_URL is required in production builds. ' +
      'Configure it in your deployment environment before serving the SPA.'
  );
})();

// How long to wait for an in-flight token refresh before giving up on
// queued requests. Prevents indefinite hangs if the refresh endpoint stalls.
const REFRESH_TIMEOUT_MS = 10_000;

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
  _retry?: boolean;
}

class APIClient {
  private client: ReturnType<typeof axios.create>;
  private isRefreshing = false;
  private refreshSubscribers: Array<{
    resolve: (token: string) => void;
    reject: (reason: unknown) => void;
  }> = [];
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Set authentication tokens (delegates to the shared auth-token store
   * so AuthContext and other subscribers re-render).
   */
  setTokens(tokens: AuthTokens | null) {
    setStoredTokens(tokens);
  }

  /**
   * Get authentication tokens from the shared auth-token store.
   */
  getTokens(): AuthTokens | null {
    return getStoredTokens();
  }

  /**
   * Clear authentication tokens.
   */
  clearTokens() {
    clearStoredTokens();
  }

  /**
   * Setup axios interceptors for auth.
   */
  private setupInterceptors() {
    // Request interceptor: Add auth header
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig & { skipAuth?: boolean }) => {
        if (config.skipAuth) {
          return config;
        }

        const tokens = this.getTokens();
        if (tokens?.accessToken) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle 401 and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Handle 401 by refreshing token
        if (
          error.response?.status === 401 &&
          !originalRequest.skipAuth &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          const tokens = this.getTokens();
          if (!tokens) {
            return Promise.reject(error);
          }

          // If a refresh is already in flight, queue this request and let
          // the in-flight refresh resolve or reject us.
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.refreshSubscribers.push({
                resolve: (newToken: string) => {
                  if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  }
                  resolve(this.client(originalRequest));
                },
                reject,
              });
            });
          }

          this.isRefreshing = true;
          this.armRefreshTimeout();

          try {
            const newTokens = await this.refreshAccessToken(tokens);
            this.setTokens(newTokens);
            this.flushRefreshSubscribers(newTokens.accessToken);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed — clear tokens, fail every queued request,
            // and reject the original.
            this.clearTokens();
            this.failRefreshSubscribers(refreshError);
            return Promise.reject(refreshError);
          } finally {
            this.disarmRefreshTimeout();
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Start the refresh-queue watchdog. If the refresh hasn't settled by
   * REFRESH_TIMEOUT_MS, every queued caller is rejected and tokens are
   * cleared so the user is forced back through login instead of hanging.
   */
  private armRefreshTimeout() {
    this.disarmRefreshTimeout();
    this.refreshTimeoutId = setTimeout(() => {
      const timeoutError = new Error(
        `Token refresh timed out after ${REFRESH_TIMEOUT_MS}ms`
      );
      this.clearTokens();
      this.failRefreshSubscribers(timeoutError);
      this.isRefreshing = false;
    }, REFRESH_TIMEOUT_MS);
  }

  private disarmRefreshTimeout() {
    if (this.refreshTimeoutId !== null) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }

  private flushRefreshSubscribers(newAccessToken: string) {
    const queued = this.refreshSubscribers;
    this.refreshSubscribers = [];
    queued.forEach(({ resolve }) => resolve(newAccessToken));
  }

  private failRefreshSubscribers(reason: unknown) {
    const queued = this.refreshSubscribers;
    this.refreshSubscribers = [];
    queued.forEach(({ reject }) => reject(reason));
  }

  /**
   * Refresh access token using refresh token.
   */
  private async refreshAccessToken(tokens: AuthTokens): Promise<AuthTokens> {
    const response = await this.client.post<RefreshTokenResponse>(
      '/auth/refresh-token',
      {
        id_token: tokens.idToken,
        refresh_token: tokens.refreshToken,
      },
      { skipAuth: true } as ExtendedAxiosRequestConfig
    );

    return {
      accessToken: response.data.access_token,
      idToken: response.data.id_token,
      refreshToken: tokens.refreshToken, // Refresh token doesn't change
    };
  }

  /**
   * Proactively refresh the access token (called on a timer before expiry).
   *
   * tRPC (fetch) and OpenSeadragon tile requests never hit the axios 401
   * interceptor, so without this the token would silently expire and every
   * tile / data call would 401. Coordinates with any in-flight interceptor
   * refresh via the same subscriber queue so the two never race.
   */
  async refreshTokens(): Promise<AuthTokens | null> {
    const tokens = this.getTokens();
    if (!tokens) return null;

    if (this.isRefreshing) {
      return new Promise<AuthTokens | null>((resolve, reject) => {
        this.refreshSubscribers.push({
          resolve: () => resolve(this.getTokens()),
          reject,
        });
      });
    }

    this.isRefreshing = true;
    this.armRefreshTimeout();
    try {
      const newTokens = await this.refreshAccessToken(tokens);
      this.setTokens(newTokens);
      this.flushRefreshSubscribers(newTokens.accessToken);
      return newTokens;
    } catch (err) {
      this.clearTokens();
      this.failRefreshSubscribers(err);
      throw err;
    } finally {
      this.disarmRefreshTimeout();
      this.isRefreshing = false;
    }
  }

  /**
   * Login with email and password.
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>(
      '/auth/login',
      {
        username: credentials.email,
        password: credentials.password,
      },
      { skipAuth: true } as ExtendedAxiosRequestConfig
    );

    return response.data;
  }

  /**
   * Logout (clear tokens).
   */
  logout() {
    this.clearTokens();
  }

  /**
   * Check if user is authenticated and token is valid.
   */
  isAuthenticated(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return false;
    }
    return !isTokenExpired(tokens.accessToken);
  }

  /**
   * Get axios instance for direct use.
   */
  getClient() {
    return this.client;
  }
}

// Create singleton instance
export const apiClient = new APIClient(API_BASE_URL);

// Export instance methods as named exports for convenience
export const { login, logout, isAuthenticated, setTokens, getTokens, clearTokens } =
  apiClient;

// Export the client instance for direct axios calls
export default apiClient.getClient();
